/**
 * Type-safe route builder system
 *
 * Provides compile-time type checking for route parameters.
 * Usage:
 *   const route = defineRoute('/dashboard/:projectId/jobs/:jobId?');
 *   route.build({ projectId: 'abc' });           // '/dashboard/abc/jobs'
 *   route.build({ projectId: 'abc', jobId: '1' }); // '/dashboard/abc/jobs/1'
 */

/**
 * API prefix for backend routes.
 * Frontend apiClient adds this prefix automatically.
 * Backend should use withApiPrefix() to add this to route patterns.
 */
export const API_PREFIX = '/api' as const;

/**
 * Prepends the API prefix to a route pattern.
 * Used by backend to ensure routes match what frontend expects.
 *
 * @example
 * withApiPrefix('/organizations') // => '/api/organizations'
 * withApiPrefix(apiRoutes.organizations.pattern) // => '/api/organizations'
 */
export function withApiPrefix<T extends string>(pattern: T): `/api${T}` {
  return `${API_PREFIX}${pattern}` as `/api${T}`;
}

// Helper type to simplify intersected object types into a single object type
type Simplify<T> = { [K in keyof T]: T[K] } & {};

/**
 * Extracts route parameters from a route pattern string.
 * - `:param` becomes required: { param: string }
 * - `:param?` becomes optional: { param?: string }
 *
 * Examples:
 * - '/dashboard/:projectId' → { projectId: string }
 * - '/dashboard/:projectId/jobs/:jobId?' → { projectId: string; jobId?: string }
 * - '/dashboard' → {}
 */
export type ExtractRouteParams<T extends string> =
  // Optional param followed by more path: :param?/rest
  T extends `${string}:${infer Param}?/${infer Rest}`
    ? Simplify<{ [K in Param]?: string } & ExtractRouteParams<`/${Rest}`>>
    : // Required param followed by more path: :param/rest
      T extends `${string}:${infer Param}/${infer Rest}`
      ? Simplify<{ [K in Param]: string } & ExtractRouteParams<`/${Rest}`>>
      : // Optional param at end: :param?
        T extends `${string}:${infer Param}?`
        ? { [K in Param]?: string }
        : // Required param at end: :param
          T extends `${string}:${infer Param}`
          ? { [K in Param]: string }
          : // No params
            // eslint-disable-next-line @typescript-eslint/no-empty-object-type
            {};

/**
 * Check if a type is an empty object (no params)
 */
type IsEmptyObject<T> = keyof T extends never ? true : false;

/**
 * Route object with pattern and type-safe build function
 */
export interface Route<TPattern extends string> {
  /** The raw route pattern with :param placeholders */
  pattern: TPattern;

  /**
   * Build a URL from the route pattern and params.
   * - If route has no params: build() takes no arguments
   * - If route has params: build(params) requires matching params object
   */
  build: IsEmptyObject<ExtractRouteParams<TPattern>> extends true
    ? () => string
    : (params: ExtractRouteParams<TPattern>) => string;
}

/**
 * Creates a type-safe route builder.
 *
 * @param pattern Route pattern with :param placeholders (e.g., '/dashboard/:projectId/jobs')
 * @returns Route object with pattern and build function
 *
 * @example
 * const route = defineRoute('/dashboard/:projectId/jobs/:jobId?');
 * route.pattern; // '/dashboard/:projectId/jobs/:jobId?'
 * route.build({ projectId: 'abc' }); // '/dashboard/abc/jobs'
 * route.build({ projectId: 'abc', jobId: '123' }); // '/dashboard/abc/jobs/123'
 */
export function defineRoute<TPattern extends string>(
  pattern: TPattern
): Route<TPattern> {
  const build = (params?: Record<string, string | undefined>): string => {
    if (!params) return pattern;

    let result = pattern as string;

    // Replace all :param and :param? placeholders
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        // Replace both :param and :param? variants
        result = result.replace(`:${key}?`, value);
        result = result.replace(`:${key}`, value);
      }
    }

    // Remove any remaining optional params that weren't provided
    result = result.replace(/\/:[^/]+\?/g, '');

    // Clean up any double slashes that might result from removal
    result = result.replace(/\/+/g, '/');

    // Remove trailing slash unless it's the root
    if (result.length > 1 && result.endsWith('/')) {
      result = result.slice(0, -1);
    }

    return result;
  };

  return {
    pattern,
    build: build as Route<TPattern>['build'],
  };
}
